import React, { useEffect, useState } from 'react';
import type { TableRowSelection } from 'antd/lib/table/interface';

import { SubTitle } from '../SubTitle';
import { Filters } from '../Filters';
import { TlidCell } from './TlidCell';
import { SelectionBar } from './SelectionBar';
import { IdentifiedFileDrawer } from './IdentifiedFileDrawer';
import { buildFileColumns } from './columns';
import { buildPaginationOptions } from './pagination';
import { useFileSelection } from './_hooks';
import { useFileSearch } from '../useFileSearch';
import { useBeraJobStepsContext } from '../../context';
import { buildFileChips, buildSegmentLabels, PARTIAL_SUBTITLE } from './utils';
import { Content, FilesTable, StepContainer } from '../styles';
import { FileForCustomReport } from '@/components/ui/Inputs/types/response/file';
import type { SaveAssignmentsParams } from './usePartialRevision';
import type { BeraReportV2, BeraSegmentAssignment } from '@/types/Bera';

const NO_FILE = '';

interface PartialSelectionProps {
	loading?: boolean;
	disabled?: boolean;
	reports: BeraReportV2[];
	selectedFileIds: string[];
	assignments: BeraSegmentAssignment[];
	onSelectFiles: (fileIds: string[]) => void;
	onSaveAssignments: (params: SaveAssignmentsParams) => void;
	onRemoveAssignment: (segmentId: string) => void;
}

export function PartialSelection({
	loading = false,
	disabled = false,
	reports,
	selectedFileIds,
	assignments,
	onSelectFiles,
	onSaveAssignments,
	onRemoveAssignment
}: Readonly<PartialSelectionProps>): JSX.Element {
	const [identifiedFileId, setIdentifiedFileId] = useState<string>(NO_FILE);

	const { isSearchingFile, handleSearchFileNameChange } = useFileSearch();
	const { page, limit, beraFiles, handlePageChange, handleIsFileSelectionPageChange } = useBeraJobStepsContext();

	useEffect(() => {
		handleIsFileSelectionPageChange(true);
		return () => {
			handleIsFileSelectionPageChange(false);
		};
	}, [handleIsFileSelectionPageChange]);

	const isLoadingFiles = loading || isSearchingFile;
	const files = beraFiles?.files?.rows ?? [];
	const segmentLabels = buildSegmentLabels(reports);
	const pageFileIds = files.map((file) => String(file.id));
	const identifiedFile = files.find((file) => String(file.id) === identifiedFileId);

	const { isAllSelected, handleToggleAllFiles, handleSelectionChange } = useFileSelection({
		pageFileIds,
		selectedFileIds,
		onSelectFiles
	});

	function handleOpenDrawer(fileId: string): void {
		setIdentifiedFileId(fileId);
	}

	function handleCloseDrawer(): void {
		setIdentifiedFileId(NO_FILE);
	}

	function handleSaveSegments(segmentIds: string[]): void {
		onSaveAssignments({ fileId: identifiedFileId, segmentIds });
		setIdentifiedFileId(NO_FILE);
	}

	function toTlidCell(file: FileForCustomReport): JSX.Element {
		const fileId = String(file.id);
		const chips = buildFileChips(segmentLabels, assignments, fileId);

		return (
			<TlidCell
				fileId={fileId}
				chips={chips}
				disabled={disabled}
				onRemove={onRemoveAssignment}
				onIdentify={handleOpenDrawer}
			/>
		);
	}

	const columns = buildFileColumns(toTlidCell);

	const rowSelection: TableRowSelection<FileForCustomReport> = {
		type: 'checkbox',
		selectedRowKeys: selectedFileIds,
		preserveSelectedRowKeys: true,
		onChange: handleSelectionChange,
		getCheckboxProps: () => ({ disabled })
	};

	const paginationOptions = buildPaginationOptions({
		page,
		limit,
		total: beraFiles?.files?.count,
		onPageChange: handlePageChange
	});

	return (
		<StepContainer>
			<SubTitle subtitle={PARTIAL_SUBTITLE} />
			<Content>
				<Filters onSearchFileNameChange={handleSearchFileNameChange} />
				<SelectionBar
					disabled={disabled}
					allSelected={isAllSelected}
					selectedCount={selectedFileIds.length}
					onToggleAll={handleToggleAllFiles}
				/>
				<FilesTable
					rowKey="id"
					columns={columns}
					dataSource={files}
					pagination={paginationOptions}
					rowSelection={rowSelection}
					loading={isLoadingFiles}
				/>
			</Content>
			{!!identifiedFile && (
				<IdentifiedFileDrawer
					fileId={identifiedFileId}
					fileName={identifiedFile.original_name}
					segmentLabels={segmentLabels}
					assignments={assignments}
					onClose={handleCloseDrawer}
					onSave={handleSaveSegments}
				/>
			)}
		</StepContainer>
	);
}
